/**
 * What `/admin/study` asks the server. The same three refusals as the rest of
 * `/admin` — not logged in, admin switched off, or an error in a sentence —
 * so the page can send a person to the right place.
 */

import type {
  StudyAuditEvent,
  StudyLiveSnapshot,
  StudyParticipantRecord,
  StudyReviewRow,
  StudyWithdrawal,
} from '@overcooked/shared'
import type { AdminOutcome, Fetcher } from './api'

export const STUDY_ADMIN_PATH = /^\/admin\/study\/?$/

async function get<T>(url: string, fetcher: Fetcher, init: RequestInit = {}): Promise<AdminOutcome<T>> {
  let res: Response
  try {
    res = await fetcher(url, { credentials: 'same-origin', ...init })
  } catch (err) {
    return { ok: false, kind: 'error', error: `Could not reach the server (${(err as Error).message}). Check your connection.` }
  }
  if (res.status === 401) return { ok: false, kind: 'unauthorised', error: 'Please log in to see this.' }
  let body: (T & { error?: string }) | null = null
  try {
    body = (await res.json()) as T & { error?: string }
  } catch {
    body = null
  }
  // Only the admin gate's 503 means admin is off; a storage 503 (a withdrawal
  // that could not read the bucket) is an error in a sentence like any other.
  if (res.status === 503 && (!body?.error || body.error.startsWith('Admin is turned off'))) {
    return { ok: false, kind: 'unconfigured', error: 'Admin is turned off on this server.' }
  }
  if (!res.ok || body === null) {
    return { ok: false, kind: 'error', error: body?.error ?? `The server returned an error (${res.status}).` }
  }
  return { ok: true, value: body }
}

export function fetchStudyLive(fetcher: Fetcher = fetch): Promise<AdminOutcome<StudyLiveSnapshot>> {
  return get<StudyLiveSnapshot>('/api/admin/study/live', fetcher)
}

export interface StudyParticipantsReply {
  participants: StudyParticipantRecord[]
  /** The manual-review table: one row per record, in the same order. */
  review: StudyReviewRow[]
  /** Stored records the server could not read, as shortened keys. */
  unreadable?: string[]
  open: boolean
  closedReason: string | null
  placeholderCodes: string[]
  allowTestPids: boolean
}

export function fetchStudyParticipants(fetcher: Fetcher = fetch): Promise<AdminOutcome<StudyParticipantsReply>> {
  return get<StudyParticipantsReply>('/api/admin/study/participants', fetcher)
}

export async function fetchStudyEvents(pid: string, fetcher: Fetcher = fetch): Promise<AdminOutcome<StudyAuditEvent[]>> {
  const outcome = await get<{ events: StudyAuditEvent[] }>(`/api/admin/study/events?pid=${encodeURIComponent(pid)}`, fetcher)
  return outcome.ok ? { ok: true, value: outcome.value.events } : outcome
}

/**
 * Withdraw a participant: the server deletes their recordings, survey and
 * audit trail, and keeps a stub of the review fields. A 409 (still in the
 * study) or 503 (storage) comes back as an error in a sentence.
 */
export function withdrawStudyParticipant(pid: string, fetcher: Fetcher = fetch): Promise<AdminOutcome<StudyWithdrawal>> {
  return get<StudyWithdrawal>(`/api/admin/study/participants/${encodeURIComponent(pid)}/withdraw`, fetcher, { method: 'POST' })
}

/** What the confirm box says before a withdrawal. */
export const WITHDRAW_CONFIRM = (pid: string): string =>
  `Withdraw ${pid}? This deletes their recordings, survey answers and event trail. ` +
  "Their partner's recordings stay. This review row stays so you can still pay them. This cannot be undone."

/** The banner, in one sentence, when any completion code is a placeholder. */
export const PLACEHOLDER_BANNER = 'Completion codes are still placeholders. Set the real Prolific codes before launch.'
